import {
    IconButton,
    Modal,
    Spinner,
    Stack,
    TooltipHost,
} from '@fluentui/react';
import React, {
    useState,
    Dispatch,
    SetStateAction,
    useCallback,
    useEffect,
} from 'react';
import { DefaultHubButton } from '../../../HubLayout/components/Utilities/DefaultHubButton';
import { usePromise } from '../../../Common/hooks/usePromise';
import {
    AgrmType,
    checkSreDeploymentControl,
    createSreDeploymentControl,
    deleteSreDeploymentControl,
} from '../../api/AGRMApi';
import { CancelToken } from 'axios';
import { DefaultLoadingHubButton } from '../../../HubLayout/components/Utilities/DefaultLoadingHubButton';

interface ISREControlModalProps {
    isModalOpen: boolean;
    setIsModalOpen: Dispatch<SetStateAction<boolean>>;
    uniqueIdentifier: string;
    type: AgrmType;
    controlUpdated?: Dispatch<SetStateAction<boolean>>;
}

export const SREControlModal: React.FC<ISREControlModalProps> = (
    props: ISREControlModalProps
) => {
    const stackTokens = { childrenGap: 10 };
    const [statusMessage, setStatusMessage] = useState<string | undefined>(
        undefined
    );
    const [errorMessage, setErrorMessage] = useState<string | undefined>(
        undefined
    );
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const modalContentStyles = {
        padding: '0px 20px',
        marginBottom: '10px',
    };

    const checkSREDeploymentControlCallback = useCallback(
        async (cancelToken?: CancelToken) => {
            if (props.uniqueIdentifier) {
                const result = await checkSreDeploymentControl(
                    props.uniqueIdentifier,
                    props.type,
                    cancelToken
                );
                setIsLoading(false);
                return result.data;
            }
        },
        [props.uniqueIdentifier, props.type]
    );

    // Fetch control status
    const [controlAlreadyExists, error, isLoaded, start] = usePromise(
        checkSREDeploymentControlCallback,
        true
    );

    const handleClick = () => {
        if (props.controlUpdated) {
            props.controlUpdated(true);
        }
        props.setIsModalOpen(!props.isModalOpen);
    };

    const createControlPolicyCallback = useCallback(
        async (cancelToken?: CancelToken) => {
            try {
                setStatusMessage(undefined);

                const result = await createSreDeploymentControl(
                    props.uniqueIdentifier,
                    props.type
                );
                setIsLoading(false);
                setStatusMessage(
                    `Successfully requested ${getBuildOrDeploymentString(
                        props.type
                    ).toLocaleLowerCase()} cancellation.`
                );
                return result;
            } catch (error) {
                setIsLoading(false);
                const errorMessage =
                    error instanceof Error ? error.message : 'Unknown error';
                setErrorMessage(
                    `Failed to request cancellation: ${errorMessage}`
                );
            } finally {
                setIsLoading(false);
            }
        },
        [props.uniqueIdentifier, props.type]
    );

    const removeControlPolicyCallback = useCallback(
        async (cancelToken?: CancelToken) => {
            try {
                setStatusMessage(undefined);

                const result = await deleteSreDeploymentControl(
                    props.uniqueIdentifier,
                    props.type
                );

                setIsLoading(false);
                setStatusMessage('Successfully removed control policy.');
                return result;
            } catch (error) {
                setIsLoading(false);
                const errorMessage =
                    error instanceof Error ? error.message : 'Unknown error';
                setErrorMessage(
                    `Failed to remove control policy: ${errorMessage}`
                );
            } finally {
                setIsLoading(false);
            }
        },
        [props.uniqueIdentifier, props.type]
    );

    const [createResult, createError, createIsLoaded, createStart] = usePromise(
        createControlPolicyCallback,
        false
    );
    const [removeResult, removeError, removeIsLoaded, removeStart] = usePromise(
        removeControlPolicyCallback,
        false
    );

    const handleCreateClick = () => {
        setIsLoading(true);
        createStart();
    };
    const handleRemoveClick = () => {
        setIsLoading(true);
        removeStart();
    };

    const getBuildOrDeploymentString = (type: AgrmType) => {
        return type === AgrmType.Build ? 'Build' : 'Deployment';
    };

    const showTitle = () => {
        if (statusMessage) {
            return statusMessage;
        }

        if (props.type === AgrmType.Deployment) {
            if (controlAlreadyExists) {
                return 'Remove Control Policy';
            } else {
                return 'Do you want to cancel this release?';
            }
        } else {
            if (controlAlreadyExists) {
                return 'Remove Control Policy';
            } else {
                return 'Do you want to cancel this build validation?';
            }
        }
    };

    return (
        <div className="SREControlModal-root">
            <Modal
                isOpen={props.isModalOpen}
                styles={{ main: { minHeight: '0' } }}
            >
                <Stack
                    horizontal
                    tokens={stackTokens}
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '0px 20px',
                    }}
                >
                    <h3>{showTitle()}</h3>
                    <IconButton
                        iconProps={{ iconName: 'Cancel' }}
                        onClick={handleClick}
                        ariaLabel="Close"
                        title="Close"
                    />
                </Stack>
                <div style={modalContentStyles}>
                    {statusMessage ? (
                        <Stack tokens={stackTokens}>
                            <DefaultHubButton
                                buttonText="Close"
                                onClick={handleClick}
                            />
                        </Stack>
                    ) : (
                        (errorMessage || error) && (
                            <Stack tokens={stackTokens}>
                                <div>{errorMessage || error?.message}</div>
                                <DefaultHubButton
                                    buttonText="Close"
                                    onClick={handleClick}
                                />
                            </Stack>
                        )
                    )}
                </div>

                {!error &&
                    !errorMessage &&
                    isLoaded &&
                    !controlAlreadyExists &&
                    !statusMessage && (
                        <Stack tokens={stackTokens} style={modalContentStyles}>
                            {props.type === AgrmType.Deployment ? (
                                <div>
                                    {' '}
                                    <div>
                                        SRM will attempt to cancel all
                                        deployments in this release. Any
                                        deployments that have already been
                                        submitted to Ev2 will not be cancelled.
                                    </div>
                                    <div>
                                        <strong>Note: </strong>To cancel
                                        individual builds, click the ellipsis on
                                        the build and select "Cancel Build
                                        Validation".
                                    </div>
                                </div>
                            ) : undefined}
                            <DefaultLoadingHubButton
                                buttonText="Confirm"
                                onClick={handleCreateClick}
                                icon={''}
                                isLoading={isLoading}
                            />
                        </Stack>
                    )}

                {!error &&
                    !errorMessage &&
                    isLoaded &&
                    controlAlreadyExists &&
                    !statusMessage && (
                        <Stack tokens={stackTokens} style={modalContentStyles}>
                            <div>
                                A cancellation control policy was found for this{' '}
                                {props.type === AgrmType.Deployment
                                    ? 'release'
                                    : 'build'}
                                . Do you wish to remove this control policy?
                            </div>

                            <div>
                                <strong>Note: </strong>
                                If your{' '}
                                {props.type === AgrmType.Deployment
                                    ? 'release'
                                    : 'build validation'}{' '}
                                has already been canceled, this action will not
                                restart it.
                            </div>

                            <DefaultLoadingHubButton
                                buttonText="Confirm"
                                onClick={handleRemoveClick}
                                icon={''}
                                isLoading={isLoading}
                            />
                        </Stack>
                    )}

                {!statusMessage && !errorMessage && !error && !isLoaded && (
                    <Spinner />
                )}
            </Modal>
        </div>
    );
};
